import { i18next, dialogs, site, bowser } from '@yola/ws-sdk';
import store from 'src/js/store';
import segment from 'src/js/modules/analytics/segment';
import dialogCallSourceIds from 'src/js/modules/website-design/constants/dialog-call-source-ids';
import upsell from 'yola-editor/src/js/modules/upsell';
import user from 'yola-editor/src/js/modules/user';
import config from 'src/js/modules/config';
import hasSiteCustomDomain from 'yola-editor/src/js/modules/publishing/verifiers/has-site-custom-domain';
import hasWLSiteCustomDomain from 'yola-editor/src/js/modules/publishing/verifiers/has-wl-site-custom-domain';
import { DESIGN_MENU_ID, helpMenuUrls } from 'yola-editor/src/js/modules/editor/constants';
import getHelpMenuItems from 'yola-editor/src/js/modules/editor/settings/get-help-menu-items';
import getHelpMenuItemClickCallbacks from 'yola-editor/src/js/modules/editor/settings/get-help-menu-item-click-callbacks';
import getDesignMenuItems from 'src/js/modules/website-design/helpers/get-design-menu-items';
import getDesignMenuItemClickCallbacks from 'src/js/modules/website-design/helpers/get-design-menu-item-click-callbacks';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';

const {
  constants: { triggerIds, events },
  trackers: { trackEvent },
} = segment;

const getMainMenuItems = (options) => {
  const {
    siteURL,
    partnerId,
    supportUrl,
    supportEmail,
    redirectToAdvertising,
    redirectToWebsite,
    redirectToAnalytics,
    redirectToDashboard,
    publicRoadmapUrl,
    upsells,
    partnerProperties,
    isFreeDomainAvailable,
    isComplianceServiceMenuItemEnabled,
    isOrderWebsiteMenuItemEnabled,
    isDifmItemWithOrderFormFlowEnabled,
    isDifmItemWithChangeRequestFlowEnabled,
    isDifmItemWithUpsellFlowEnabled,
  } = options;

  const state = store.getState();
  const isWL = user.selectors.getWhiteLabelStatus(state);
  const withPartnersDomainUpsell = user.verifiers.hasPartnersDomainUpsell(state);
  const contactSupportUrl = user.selectors.getContactSupportUrl(state);
  const termsOfUseUrl = user.selectors.getTermsOfUseUrl(state);
  const privacyPolicyUrl = user.selectors.getPrivacyPolicyUrl(state);
  const helpCenterConfig = config.selectors.getHelpCenterConfig(state);
  const helpMenuItemClickCallbacks = getHelpMenuItemClickCallbacks(triggerIds.MAIN_MENU);
  const designMenuItemClickCallbacks = getDesignMenuItemClickCallbacks(
    dialogCallSourceIds.APP_MAIN_MENU
  );
  const isTouchDevice = bowser.mobile || bowser.tablet;

  const items = [
    {
      order: 1,
      disabled: !siteURL,
      href: siteURL,
      target: '_blank',
      heading: i18next.t('View published website'),
      id: 'view-publish',
      icon: {
        glyph: 'view-publish-site',
        type: 'inherit',
      },
    },
    {
      type: 'divider',
      order: 2,
    },

    /*
     * In order to make `Exit editor` a last item in the menu,
     * even in cases when other items is added, we set so big order
     * */
    {
      type: 'divider',
      order: 98,
    },
    {
      order: 99,
      heading: redirectToDashboard ? i18next.t('Go to dashboard') : i18next.t('Exit editor'),
      onClick: redirectToDashboard || redirectToWebsite,
      id: 'dashboard',
      icon: {
        glyph: 'home',
        width: 22,
        height: 22,
        strokeWidth: 1.3,
      },
    },
  ];

  if (redirectToAdvertising) {
    items.push({
      heading: i18next.t('Advertising'),
      onClick: redirectToAdvertising,
      order: 85,
      id: 'advertising',
      icon: {
        glyph: 'advertising',
        width: 24,
        height: 24,
        strokeWidth: 1.3,
      },
    });
  }

  if (redirectToAnalytics) {
    items.push({
      heading: i18next.t('Analytics'),
      onClick: redirectToAnalytics,
      order: 87,
      id: 'analytics',
      icon: {
        glyph: 'analytics',
        width: 24,
        height: 24,
        strokeWidth: 1.3,
      },
    });
  }

  items.push({
    heading: i18next.t('Help'),
    order: 90,
    target: '_blank',
    subMenu: getHelpMenuItems({
      isWL,
      partnerId,
      contactSupportUrl,
      supportEmail,
      gettingStartedUrl: helpMenuUrls.GETTING_STARTED,
      helpCenterUrl: supportUrl,
      publicRoadmapUrl,
      termsOfUseUrl,
      privacyPolicyUrl,
      isComplianceServiceMenuItemEnabled,
      isOrderWebsiteMenuItemEnabled,
      helpMenuItemClickCallbacks,
    }),
    id: 'help',
    iconGlyph: 'help',
  });

  items.push({
    heading: i18next.t('Design'),
    order: 70,
    target: '_blank',
    labelFor: DESIGN_MENU_ID,
    subMenu: getDesignMenuItems(designMenuItemClickCallbacks),
    id: 'design',
    iconGlyph: 'design',
  });

  if (isDifmItemWithChangeRequestFlowEnabled || isDifmItemWithOrderFormFlowEnabled) {
    items.push({
      type: 'divider',
      order: 91,
    });

    const hasPopover = isDifmItemWithChangeRequestFlowEnabled && !isTouchDevice;

    items.push({
      heading: i18next.t('Professional services'),
      order: 92,
      id: 'professional-services',
      ...(isDifmItemWithChangeRequestFlowEnabled && {
        href: helpCenterConfig.nn_difm_request_changes_url,
        target: '_blank',
      }),
      icon: {
        glyph: 'magic',
      },
      ...(hasPopover && {
        popoverProps: {
          title: i18next.t('Request website changes'),
          description: i18next.t('Send a request and your designer will update your site for you'),
        },
      }),
      onClick: () => {
        if (isDifmItemWithChangeRequestFlowEnabled) {
          trackEvent(events.DIFM_CHANGE_REQUEST_BUTTON_CLICKED, {
            businessCategory: site.accessors.getBusinessCategory(),
            triggerId: triggerIds.MAIN_MENU,
          });

          // Since we use href to redirect to help center, we don't need to show any dialogs
          return;
        }

        trackEvent(events.DIFM_BUTTON_CLICKED, {
          businessCategory: site.accessors.getBusinessCategory(),
          triggerId: triggerIds.MAIN_MENU,
        });

        dialogs.operations.show(dialogTypes.DIFM_ORDER_FORM_DIALOG);
      },
    });
  }

  const banners = [];
  const defaultUpsell = upsells.default;
  const { submitUserActionUrl, freeHostingDomains } = partnerProperties || {};
  const isInAppDomainsConfigured = Boolean(submitUserActionUrl && freeHostingDomains);
  let withUpsell = !hasSiteCustomDomain(state);

  if (isWL) {
    const hasCustomDomain = hasWLSiteCustomDomain(state);
    withUpsell = isInAppDomainsConfigured && !hasCustomDomain;
  }

  if (isDifmItemWithUpsellFlowEnabled) {
    const professionalServicesBanner = {
      id: 'professional-services',
      isInteractive: true,
      appearance: 'accent',
      onClick: () => {
        trackEvent(events.DIFM_BUTTON_CLICKED, {
          businessCategory: site.accessors.getBusinessCategory(),
          triggerId: triggerIds.MAIN_MENU,
        });
        dialogs.operations.show(dialogTypes.DIFM_UPSELL_DIALOG);
      },
      captions: {
        title: i18next.t('Professional services'),
        description: i18next.t('Let us translate your vision into a professional website'),
        actionLabel: i18next.t('Learn more'),
      },
      icon: {
        name: 'magic',
      },
    };

    banners.push(professionalServicesBanner);
  }

  const upgradeBanner = {
    id: 'upgrade',
    isInteractive: true,
    onClick: defaultUpsell.onUpgrade || (() => {}),
    captions: {
      title: i18next.t('Upgrade subscription'),
      description: i18next.t('Gain access to new features by upgrading your subscription'),
      actionLabel: i18next.t('Upgrade Now'),
    },
    icon: {
      name: 'upgrade',
      strokeWidth: 1.3,
    },
  };
  const domainBanner = {
    id: 'domain',
    isInteractive: true,
    onClick: () => {
      const triggerId = upsell.constants.triggerIds.MAIN_MENU;

      if (withPartnersDomainUpsell) {
        store.dispatch(upsell.actions.startWLDomainPurchase(triggerId));
      } else {
        store.dispatch(upsell.actions.startB2CDomainPurchase(triggerId));
      }
    },
    captions: {
      title: i18next.t('Get a custom domain'),
      description: i18next.t('Improve credibility of your site by connecting a custom domain'),
      actionLabel: isFreeDomainAvailable ? i18next.t('Get free domain') : i18next.t('Buy a domain'),
    },
    icon: {
      name: 'domain',
      strokeWidth: 1.3,
    },
  };

  if (Object.keys(defaultUpsell).length) {
    banners.push(upgradeBanner);
  }
  if (withPartnersDomainUpsell || (!isWL && withUpsell)) {
    banners.push(domainBanner);
  }

  return {
    items,
    banners: banners.length ? banners : null,
  };
};

export default getMainMenuItems;
